// src/ConsumerApp/Screens/Auth/LoginModal.js

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthModal } from '../../context/AuthModalContext';
import { requestPermission } from '../../../push-notifications';
import { updateUserInServiceWorker } from '../../../utils/serviceWorkerUtils';

// SVGs
import X from '../../../assets/icons/X.svg';
import LogoBB from '../../../assets/icons/logoBB.svg';

const BASE_URL = process.env.REACT_APP_FORE_BRICKBUCKET || '';
const LOGIN_URL = `${BASE_URL}customer/customer-login`;
const VERIFY_OTP_URL = `${BASE_URL}customer/verify-otp-and-login`;
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.brickbucket.buyer';

export default function LoginModal() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isLoginModalOpen, closeLoginModal, syncGuestCartToServer } = useAuthModal();

  const [step, setStep] = useState('MOBILE'); // 'MOBILE' | 'OTP'
  const [mobileNumber, setMobileNumber] = useState('');
  const [otp, setOtp] = useState(['', '', '', '']); // 4 Digits
  const [timer, setTimer] = useState(60);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const inputRefs = useRef([]);

  useEffect(() => {
    let interval = null;
    if (step === 'OTP' && timer > 0) {
      interval = setInterval(() => setTimer((t) => t - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [step, timer]);

  useEffect(() => {
    if (isLoginModalOpen) {
      setStep('MOBILE');
      setOtp(['', '', '', '']);
      setErrorMessage('');
      setTimer(60);
    }
  }, [isLoginModalOpen]);

  if (!isLoginModalOpen) return null;

  const handleSendOtp = async (forceLogin = false) => {
    if (mobileNumber.length !== 10) {
      setErrorMessage('Mobile number must be 10 digits.');
      return;
    }

    if (!['6', '7', '8', '9'].includes(mobileNumber[0])) {
      setErrorMessage('Mobile number must start with 6, 7, 8, or 9.');
      return;
    }

    setErrorMessage('');
    setLoading(true);

    try {
      const res = await fetch(LOGIN_URL, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ mobile_number: mobileNumber, forceLogin }),
      });

      const data = await res.json();

      if (res.status === 409 && !forceLogin) {
        return handleSendOtp(true);
      }

      if (res.ok) {
        setStep('OTP');
        setTimer(60);
        setTimeout(() => inputRefs.current[0]?.focus(), 150);
      } else if (res.status === 404) {
        sessionStorage.setItem('unregisteredMobile', mobileNumber);
        closeLoginModal();
        navigate('/consumer/UserDetails');
      } else {
        setErrorMessage(data?.message || 'Login failed. Please try again.');
      }
    } catch (err) {
      console.error('OTP Send error:', err);
      setErrorMessage('Unable to connect. Please check your internet connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (forceLogin = false) => {
    const otpString = otp.join('');
    if (otpString.length !== 4) {
      setErrorMessage('Please enter the complete 4-digit code.');
      return;
    }

    setErrorMessage('');
    setLoading(true);

    try {
      const res = await fetch(VERIFY_OTP_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mobile_number: mobileNumber,
          otp: otpString,
          forceLogin: true,
        }),
      });

      const data = await res.json();
      const userObj = data?.user || data?.data || data;
      const isSuccessfulLogin = res.ok || (res.status === 409 && (userObj?._id || userObj?.token));

      if (!isSuccessfulLogin) {
        setErrorMessage(data?.message || 'Invalid verification code.');
        return;
      }

      const customerRecord = {
        ...userObj,
        _id: userObj._id || userObj.customerId || userObj.id,
      };

      localStorage.setItem('userData', JSON.stringify(customerRecord));
      sessionStorage.setItem('userData', JSON.stringify(customerRecord));
      sessionStorage.setItem('customerId', customerRecord._id);
      sessionStorage.setItem('token', customerRecord.token);
      sessionStorage.setItem('hubId', customerRecord.hubId || '');
      sessionStorage.setItem('roleId', customerRecord.roleId || '');

      window.dispatchEvent(new Event('auth-state-change'));

      try {
        if (typeof requestPermission === 'function') requestPermission();
        if (typeof updateUserInServiceWorker === 'function') updateUserInServiceWorker(customerRecord._id);
      } catch (e) {
        console.warn('Service worker setup skipped:', e);
      }

      await syncGuestCartToServer(customerRecord._id);

      queryClient.invalidateQueries({ queryKey: ['bucketSummary', customerRecord._id] });
      queryClient.invalidateQueries({ queryKey: ['defaultDeliveryAddress', customerRecord._id] });
      queryClient.invalidateQueries({ queryKey: ['profile', customerRecord._id] });

      closeLoginModal();

      if (customerRecord.customer_status === 1) {
        navigate('/consumer/UserDetails');
      } else if (customerRecord.customer_status === 2) {
        navigate('/consumer/Approved');
      }
    } catch (err) {
      console.error('Verify error:', err);
      setErrorMessage('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    if (value.length > 1) {
      const chars = value.slice(0, 4).split('');
      const updated = [...otp];
      chars.forEach((c, i) => {
        if (i < 4) updated[i] = c;
      });
      setOtp(updated);
      const nextIdx = Math.min(chars.length, 3);
      inputRefs.current[nextIdx]?.focus();
      return;
    }

    const updated = [...otp];
    updated[index] = value;
    setOtp(updated);

    if (value && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  return (
    <div style={styles.overlay} onClick={closeLoginModal}>
      <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button style={styles.closeBtn} onClick={closeLoginModal} type="button">
          <img src={X} alt="close" style={{ width: 14, height: 14 }} />
        </button>

        {/* ── Left Column: Clean Brand & Value Props ── */}
        <div style={styles.leftVisualCol}>
          <img src={LogoBB} alt="Brickbucket" style={styles.brandLogo} />
          <h2 style={styles.leftHeading}>Building materials delivered in minutes</h2>
          <p style={styles.leftSubtext}>
            Order cement, plumbing, electrical, and construction supplies directly to your site.
          </p>

          <div style={styles.perksList}>
            <div style={styles.perkItem}>
              <span style={styles.perkIcon}>⚡</span>
              <span>30–90 min site delivery</span>
            </div>
            <div style={styles.perkItem}>
              <span style={styles.perkIcon}>🧾</span>
              <span>100% verified GST invoices</span>
            </div>
            <div style={styles.perkItem}>
              <span style={styles.perkIcon}>💳</span>
              <span>Secure online payment</span>
            </div>
          </div>
        </div>

        {/* ── Right Column: Input Flow + High-Contrast App Badges ── */}
        <div style={styles.rightFormCol}>
          <div style={styles.formMainSection}>
            {step === 'MOBILE' ? (
              <>
                <div style={styles.formHeader}>
                  <h3 style={styles.mainTitle}>Login or Sign Up</h3>
                  <span style={styles.subTitle}>
                    Enter your mobile number to checkout or access your site orders
                  </span>
                </div>

                {errorMessage && <div style={styles.errorBanner}>{errorMessage}</div>}

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSendOtp(false);
                  }}
                  style={styles.formContainer}
                >
                  <div style={styles.phoneInputRow}>
                    <div style={styles.countryCodeBadge}>
                      <span>🇮🇳 +91</span>
                    </div>
                    <input
                      type="tel"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={10}
                      placeholder="Enter 10 digit mobile number"
                      value={mobileNumber}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                        setMobileNumber(val);
                        setErrorMessage('');
                      }}
                      style={styles.mobileInput}
                      autoFocus
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading || mobileNumber.length !== 10}
                    style={{
                      ...styles.actionBtn,
                      opacity: mobileNumber.length === 10 && !loading ? 1 : 0.55,
                      cursor: mobileNumber.length === 10 && !loading ? 'pointer' : 'not-allowed',
                    }}
                  >
                    {loading ? 'Sending OTP...' : 'Continue'}
                  </button>
                </form>

                <span style={styles.legalDisclaimer}>
                  By continuing, you agree to our <strong>Terms of Service</strong> & <strong>Privacy Policy</strong>.
                </span>
              </>
            ) : (
              <>
                <div style={styles.formHeader}>
                  <h3 style={styles.mainTitle}>Verify OTP</h3>
                  <span style={styles.subTitle}>
                    We sent a 4-digit code to <strong>+91 {mobileNumber}</strong>
                  </span>
                </div>

                {errorMessage && <div style={styles.errorBanner}>{errorMessage}</div>}

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleVerifyOtp(false);
                  }}
                  style={styles.formContainer}
                >
                  <div style={styles.otpGrid}>
                    {otp.map((digit, idx) => (
                      <input
                        key={idx}
                        ref={(el) => (inputRefs.current[idx] = el)}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOtpChange(idx, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(e, idx)}
                        style={styles.otpBox}
                      />
                    ))}
                  </div>

                  <button
                    type="submit"
                    disabled={loading || otp.join('').length !== 4}
                    style={{
                      ...styles.actionBtn,
                      opacity: otp.join('').length === 4 && !loading ? 1 : 0.55,
                      cursor: otp.join('').length === 4 && !loading ? 'pointer' : 'not-allowed',
                    }}
                  >
                    {loading ? 'Verifying...' : 'Verify OTP'}
                  </button>
                </form>

                <div style={styles.otpFooterActions}>
                  <button
                    type="button"
                    style={styles.changeNumberBtn}
                    onClick={() => {
                      setStep('MOBILE');
                      setErrorMessage('');
                    }}
                  >
                    Change phone number
                  </button>

                  <div style={styles.resendWrapper}>
                    {timer > 0 ? (
                      <span style={styles.timerText}>
                        Resend in <strong style={{ color: '#E9001E' }}>{timer}s</strong>
                      </span>
                    ) : (
                      <button
                        type="button"
                        style={styles.resendBtn}
                        onClick={() => handleSendOtp(false)}
                      >
                        Resend OTP
                      </button>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* ── High-Contrast App Store Badges (Zepto Style) ── */}
          <div style={styles.appDownloadSection}>
            <span style={styles.appDownloadHeading}>Order faster & easier everytime with the Brickbucket app</span>
            <div style={styles.badgesRow}>
              {/* Google Play Store Badge */}
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={styles.blackBadge}
              >
                <svg style={styles.playStoreSvg} viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M3.6 1.8L13.8 12 3.6 22.2c-.4-.4-.6-.9-.6-1.4V3.2c0-.5.2-1 .6-1.4z" />
                  <path fill="#FBBC04" d="M17.4 8.4l-3.6 3.6 3.6 3.6 4.1-2.4c1.2-.7 1.2-1.8 0-2.4l-4.1-2.4z" />
                  <path fill="#EA4335" d="M3.6 1.8l10.2 10.2 3.6-3.6-12.4-7.1c-.5-.3-1-.1-1.4.5z" />
                  <path fill="#34A853" d="M13.8 12l-10.2 10.2c.4.6.9.8 1.4.5l12.4-7.1-3.6-3.6z" />
                </svg>
                <div style={styles.badgeTextWrap}>
                  <span style={styles.badgeTopText}>GET IT ON</span>
                  <span style={styles.badgeMainText}>Google Play</span>
                </div>
              </a>

              {/* iOS App Store Badge (Coming Soon) */}
              <div style={styles.blackBadgeMuted} title="iOS App coming soon">
                <svg style={styles.appleSvg} viewBox="0 0 24 24" fill="#FFFFFF">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 6.37c.61-.75 1.04-1.8 0.92-2.85-.9.04-2 .6-2.65 1.35-.58.66-1.09 1.73-.96 2.76 1.01.08 2.06-.51 2.69-1.26z" />
                </svg>
                <div style={styles.badgeTextWrap}>
                  <span style={styles.badgeTopText}>Download on the</span>
                  <span style={styles.badgeMainText}>App Store</span>
                </div>
                <span style={styles.comingSoonTag}>SOON</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.65)',
    zIndex: 999999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
    backdropFilter: 'blur(3px)',
  },
  modalCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 24,
    width: '100%',
    maxWidth: 820,
    display: 'flex',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0 25px 60px -15px rgba(0, 0, 0, 0.3)',
  },
  closeBtn: {
    position: 'absolute',
    top: 18,
    right: 18,
    background: '#F1F5F9',
    border: 'none',
    width: 34,
    height: 34,
    borderRadius: 17,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    zIndex: 10,
    transition: 'background-color 0.15s ease',
  },
  leftVisualCol: {
    flex: 1,
    backgroundColor: '#FFF7F7',
    padding: '44px 36px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    borderRight: '1px solid #FFE4E6',
  },
  brandLogo: {
    height: 48,
    width: 'auto',
    alignSelf: 'flex-start',
    marginBottom: 24,
  },
  leftHeading: {
    fontSize: 22,
    fontWeight: '800',
    color: '#2B2A29',
    lineHeight: '28px',
    margin: '0 0 10px 0',
  },
  leftSubtext: {
    fontSize: 13,
    color: '#64748B',
    lineHeight: '19px',
    margin: '0 0 26px 0',
  },
  perksList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  perkItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    fontSize: 13,
    fontWeight: '600',
    color: '#2B2A29',
  },
  perkIcon: {
    fontSize: 15,
  },
  rightFormCol: {
    flex: 1.2,
    padding: '40px 38px 24px 38px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  formMainSection: {
    display: 'flex',
    flexDirection: 'column',
  },
  formHeader: {
    marginBottom: 20,
  },
  mainTitle: {
    fontSize: 22,
    fontWeight: '800',
    color: '#1E293B',
    margin: '0 0 6px 0',
  },
  subTitle: {
    fontSize: 13,
    color: '#64748B',
    lineHeight: '18px',
  },
  errorBanner: {
    backgroundColor: '#FEF2F2',
    border: '1px solid #FCA5A5',
    color: '#DC2626',
    borderRadius: 8,
    padding: '8px 12px',
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 16,
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  phoneInputRow: {
    display: 'flex',
    alignItems: 'center',
    border: '1.5px solid #CBD5E1',
    borderRadius: 12,
    height: 50,
    overflow: 'hidden',
  },
  countryCodeBadge: {
    padding: '0 14px',
    backgroundColor: '#F8FAFC',
    borderRight: '1px solid #CBD5E1',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    fontSize: 14,
    fontWeight: '600',
    color: '#2B2A29',
  },
  mobileInput: {
    flex: 1,
    height: '100%',
    border: 'none',
    outline: 'none',
    padding: '0 14px',
    fontSize: 15,
    fontWeight: '600',
    letterSpacing: '0.4px',
    color: '#2B2A29',
  },
  actionBtn: {
    backgroundColor: '#E9001E',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 12,
    height: 48,
    fontSize: 14.5,
    fontWeight: '700',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
  },
  legalDisclaimer: {
    fontSize: 11,
    color: '#94A3B8',
    textAlign: 'center',
    lineHeight: '16px',
    marginTop: 14,
  },
  otpGrid: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 10,
    margin: '6px 0',
  },
  otpBox: {
    width: 56,
    height: 60,
    textAlign: 'center',
    fontSize: 22,
    fontWeight: '800',
    color: '#2B2A29',
    border: '1.5px solid #CBD5E1',
    borderRadius: 12,
    outline: 'none',
    backgroundColor: '#FAFAFA',
  },
  otpFooterActions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 16,
  },
  changeNumberBtn: {
    background: 'none',
    border: 'none',
    color: '#0F4392',
    fontSize: 12.5,
    fontWeight: '600',
    cursor: 'pointer',
    padding: 0,
  },
  resendWrapper: {
    fontSize: 12.5,
  },
  timerText: {
    color: '#64748B',
  },
  resendBtn: {
    background: 'none',
    border: 'none',
    color: '#E9001E',
    fontSize: 12.5,
    fontWeight: '700',
    cursor: 'pointer',
    padding: 0,
  },
  appDownloadSection: {
    marginTop: 26,
    paddingTop: 18,
    borderTop: '1px solid #F1F5F9',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 10,
  },
  appDownloadHeading: {
    fontSize: 11.5,
    fontWeight: '600',
    color: '#64748B',
    textAlign: 'center',
  },
  badgesRow: {
    display: 'flex',
    gap: 12,
    width: '100%',
    justifyContent: 'center',
  },
  blackBadge: {
    flex: 1,
    maxWidth: 156,
    height: 44,
    backgroundColor: '#000000',
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: '0 12px',
    gap: 10,
    textDecoration: 'none',
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.18)',
    transition: 'transform 0.15s ease, opacity 0.15s ease',
  },
  blackBadgeMuted: {
    flex: 1,
    maxWidth: 156,
    height: 44,
    backgroundColor: '#1E293B',
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: '0 12px',
    gap: 10,
    cursor: 'not-allowed',
    position: 'relative',
    opacity: 0.9,
  },
  playStoreSvg: {
    width: 22,
    height: 22,
    flexShrink: 0,
  },
  appleSvg: {
    width: 22,
    height: 22,
    flexShrink: 0,
  },
  badgeTextWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    lineHeight: 1,
  },
  badgeTopText: {
    fontSize: 8,
    color: '#D1D5DB',
    letterSpacing: '0.4px',
    marginBottom: 2,
    textTransform: 'uppercase',
  },
  badgeMainText: {
    fontSize: 12,
    fontWeight: '700',
    color: '#FFFFFF',
    letterSpacing: '0.2px',
  },
  comingSoonTag: {
    position: 'absolute',
    top: -6,
    right: -4,
    backgroundColor: '#E9001E',
    color: '#FFFFFF',
    fontSize: 8.5,
    fontWeight: '800',
    padding: '1px 5px',
    borderRadius: 4,
    letterSpacing: '0.5px',
  },
};